/**
 * Blok Basismap in Instellingen > Meer (Frank, 18 oktober 2026, glasronde 0.9.169).
 *
 * Het eerste-startscherm (H3) vraagt eenmalig om een basismap; daarna was hij
 * nergens meer te zien of te wijzigen. Dit blok toont het gekozen pad en heeft
 * een knop Kies map, met dezelfde mapkiezer en dezelfde route als H3.
 *
 * DE KNOP STAAT ER ALLEEN IN DE MAC-APP EN ALLEEN VOOR EEN EIGENAAR. Waar de
 * kiezer ontbreekt (telefoon, browser) staat er een zin waarom; of iemand mag
 * zetten zegt de backend (`mag_zetten`), en de PUT weigert een lid hoe dan ook.
 *
 * Kiezen wordt meteen bewaard: er is geen tekstveld en dus niets halfs om op te
 * slaan. Een geweigerde map laat het oude pad staan en zegt waarom.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { desktopKiezer } from "./EersteStart";

/** Wat het blok onder het pad laat zien: "knop", "geen-mac" of "geen-eigenaar". PUUR. */
export function blokStand(stand, opDeMac) {
  if (stand?.mag_zetten !== true) return "geen-eigenaar";
  if (!opDeMac) return "geen-mac";
  return "knop";
}

export default function BasismapBlok() {
  const { t } = useTaal();
  const [stand, setStand] = useState(null);
  const [fout, setFout] = useState("");
  const [bezig, setBezig] = useState(false);
  const kiezer = desktopKiezer();

  const laden = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/basismap`);
      setStand(r?.data || null);
    } catch {
      setStand(null);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const kies = async () => {
    const gekozen = await kiezer();
    if (!gekozen) return;
    setBezig(true);
    setFout("");
    try {
      await axios.put(`${API}/settings/basismap`, { basismap: gekozen });
      await laden();
    } catch (e) {
      setFout(e?.response?.status === 403
        ? t("Alleen een eigenaar kan de basismap kiezen.")
        : t("Dit is geen bestaande map met een volledig pad."));
    } finally {
      setBezig(false);
    }
  };

  if (!stand) return null;
  const soort = blokStand(stand, kiezer !== null);
  return (
    <div className="border rounded-lg p-4 space-y-2" data-testid="basismap-blok">
      <h4 className="font-medium text-sm">{t("Basismap")}</h4>
      <p className="text-xs text-muted-foreground">
        {t("De map waarin je vault staat. Kennisbanken lezen hun map ten opzichte van deze map; kies je een andere, dan zoeken ze hun mappen daar.")}
      </p>
      <p className="text-sm font-mono break-all" data-testid="basismap-pad">
        {stand.basismap || t("Nog geen basismap gekozen.")}
      </p>
      {soort === "knop" && (
        <Button type="button" variant="outline" size="sm" onClick={kies} disabled={bezig}
                data-testid="basismap-kies">
          {t("Kies map...")}
        </Button>
      )}
      {soort === "geen-mac" && (
        <p className="text-xs text-muted-foreground" data-testid="basismap-uitleg">
          {t("Wijzigen kan alleen in de app op de Mac.")}
        </p>
      )}
      {soort === "geen-eigenaar" && (
        <p className="text-xs text-muted-foreground" data-testid="basismap-uitleg">
          {t("Alleen een eigenaar kan de basismap kiezen.")}
        </p>
      )}
      {fout && <p className="text-sm text-destructive" role="alert">{fout}</p>}
    </div>
  );
}
